import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeProvider, useTheme, Topbar, LoadingScreen } from '../shared/DashWidgets';
import Sidebar from '../shared/Sidebar';
import { buildStyles, COLORS } from '../shared/DashTheme';
import api from '../../services/api';

const DEPARTMENTS = [
  "Architecture Engineering","Basic Sciences And Related Studies","Bio-Medical Engineering",
  "Chemical Engineering","City And Regional Planning","Civil Engineering",
  "Computer Systems Engineering","Electrical Engineering","Electronics Engineering",
  "Industrial Engineering","Mechanical Engineering","Mechatronic Engineering",
  "Metallurgy And Materials Engineering","Mining Engineering",
  "Petroleum And Natural Gas Engineering","Software Engineering",
  "Telecommunication Engineering","Textile Engineering"
];

const LOGIN_URL = `${window.location.origin}/authority-login`;

// ── Success card shown after creation ─────────────────────────────────────────
function SuccessCard({ created, tempPassword, onCreateAnother, navigate, s }) {
  const [copied, setCopied] = useState(false);
  const [pwVisible, setPwVisible] = useState(false);
  const [shareTarget, setShareTarget] = useState(null); // 'email' | 'whatsapp'
  const [shareInput, setShareInput] = useState('');
  const [shareError, setShareError] = useState('');

  const typeLabel = created.authority_type === 'university' ? 'University Authority' : 'Department Authority (Chairman)';

  const shareText = `Hello ${created.name},\n\nYour UniResolve ${typeLabel} account has been created.\n\nLogin Details:\n• Portal: ${LOGIN_URL}\n• Email: ${created.email}\n• Temporary Password: ${tempPassword}\n\nPlease log in and change your password immediately.\n\nRegards,\nUniResolve Administration`;

  const handleCopy = () => {
    navigator.clipboard.writeText(tempPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const openShare = (type) => {
    setShareTarget(type);
    setShareInput(type === 'email' ? created.email : '');
    setShareError('');
  };

  const confirmShare = () => {
    if (shareTarget === 'email') {
      if (!shareInput || !/\S+@\S+\.\S+/.test(shareInput)) {
        setShareError('Please enter a valid email address.');
        return;
      }
      const subject = encodeURIComponent('Your UniResolve Authority Account Credentials');
      const body = encodeURIComponent(shareText);
      window.open(`mailto:${shareInput}?subject=${subject}&body=${body}`);
    } else {
      const digits = shareInput.replace(/\D/g, '');
      if (!digits || digits.length < 7) {
        setShareError('Please enter a valid WhatsApp number with country code (e.g. 923001234567).');
        return;
      }
      window.open(`https://wa.me/${digits}?text=${encodeURIComponent(shareText)}`);
    }
    setShareTarget(null);
    setShareInput('');
  };

  return (
    <div style={{ maxWidth: 560, margin: '0 auto' }}>
      {/* Success banner */}
      <div style={{ textAlign: 'center', marginBottom: 28 }}>
        <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'linear-gradient(135deg,rgba(99,102,241,0.3),rgba(99,102,241,0.1))', border: '2px solid rgba(99,102,241,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, margin: '0 auto 14px' }}>
          ✓
        </div>
        <div style={{ fontSize: 22, fontWeight: 800, color: '#fff', marginBottom: 6 }}>Authority Member Created</div>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>Share the login credentials with the new authority member</div>
      </div>

      {/* Details card */}
      <div style={{ ...s.card, padding: '24px 28px', marginBottom: 16, border: '1px solid rgba(99,102,241,0.3)', background: 'rgba(99,102,241,0.06)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 22, paddingBottom: 18, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ width: 52, height: 52, borderRadius: '50%', background: created.authority_type === 'university' ? 'rgba(99,102,241,0.3)' : 'rgba(245,158,11,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 800, color: '#fff', flexShrink: 0 }}>
            {created.name?.[0]?.toUpperCase()}
          </div>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#fff' }}>{created.name}</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 3 }}>{created.email}</div>
          </div>
          <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 700, padding: '4px 12px', borderRadius: 20, background: created.authority_type === 'university' ? 'rgba(99,102,241,0.25)' : 'rgba(245,158,11,0.25)', color: created.authority_type === 'university' ? '#a5b4fc' : '#fcd34d', border: `1px solid ${created.authority_type === 'university' ? 'rgba(99,102,241,0.4)' : 'rgba(245,158,11,0.4)'}` }}>
            {created.authority_type?.toUpperCase()}
          </span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[
            { label: 'Role', value: typeLabel },
            { label: 'Department', value: created.department || '—' },
            { label: 'Login Portal', value: LOGIN_URL, mono: true },
            { label: 'Email', value: created.email, mono: true },
          ].map(({ label, value, mono }) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</span>
              <span style={{ fontSize: 13, color: '#fff', fontFamily: mono ? 'monospace' : 'inherit', fontWeight: mono ? 500 : 600 }}>{value}</span>
            </div>
          ))}

          {/* Password row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderRadius: 10, background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)', marginTop: 4 }}>
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Temp Password</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 15, fontWeight: 800, color: COLORS.amber, fontFamily: 'monospace', letterSpacing: 1 }}>
                {pwVisible ? tempPassword : '••••••••'}
              </span>
              <button onClick={() => setPwVisible(v => !v)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.5)', fontSize: 14, padding: '2px 4px' }}>
                {pwVisible ? '🙈' : '👁️'}
              </button>
              <button onClick={handleCopy} style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6, background: copied ? 'rgba(16,185,129,0.3)' : 'rgba(255,255,255,0.12)', border: `1px solid ${copied ? 'rgba(16,185,129,0.5)' : 'rgba(255,255,255,0.2)'}`, color: copied ? '#6ee7b7' : '#fff', cursor: 'pointer', fontWeight: 600 }}>
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 14, padding: '8px 12px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', fontSize: 11, color: '#fca5a5' }}>
          ⚠ This password is shown only once. Make sure to share it with the authority member before leaving this page.
        </div>
      </div>

      {/* Share buttons */}
      <div style={{ ...s.card, padding: '20px 24px', marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', marginBottom: 14 }}>Share Login Details</div>
        <div style={{ display: 'flex', gap: 10, marginBottom: shareTarget ? 14 : 0 }}>
          <button className="ur-btn-hover" onClick={() => openShare('email')}
            style={{ flex: 1, padding: '12px 16px', borderRadius: 10, background: shareTarget === 'email' ? 'rgba(59,130,246,0.35)' : 'rgba(59,130,246,0.2)', border: `1px solid ${shareTarget === 'email' ? 'rgba(59,130,246,0.7)' : 'rgba(59,130,246,0.4)'}`, color: '#93c5fd', cursor: 'pointer', fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <span style={{ fontSize: 18 }}>✉️</span> Share via Email
          </button>
          <button className="ur-btn-hover" onClick={() => openShare('whatsapp')}
            style={{ flex: 1, padding: '12px 16px', borderRadius: 10, background: shareTarget === 'whatsapp' ? 'rgba(37,211,102,0.3)' : 'rgba(37,211,102,0.15)', border: `1px solid ${shareTarget === 'whatsapp' ? 'rgba(37,211,102,0.6)' : 'rgba(37,211,102,0.35)'}`, color: '#86efac', cursor: 'pointer', fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <span style={{ fontSize: 18 }}>💬</span> Share via WhatsApp
          </button>
        </div>

        {shareTarget && (
          <div style={{ padding: '16px', borderRadius: 10, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', marginTop: 4 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.7)', marginBottom: 8 }}>
              {shareTarget === 'email' ? '📧 Enter recipient email address' : '📱 Enter WhatsApp number (with country code)'}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                autoFocus
                type={shareTarget === 'email' ? 'email' : 'tel'}
                value={shareInput}
                onChange={e => { setShareInput(e.target.value); setShareError(''); }}
                placeholder={shareTarget === 'email' ? 'e.g. xyz@authority.edu' : 'e.g. 923001234567'}
                style={{ flex: 1, padding: '9px 12px', borderRadius: 8, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', fontSize: 13, outline: 'none' }}
                onKeyDown={e => e.key === 'Enter' && confirmShare()}
              />
              <button onClick={confirmShare} className="ur-btn-hover"
                style={{ padding: '9px 18px', borderRadius: 8, background: shareTarget === 'email' ? 'rgba(59,130,246,0.4)' : 'rgba(37,211,102,0.35)', border: 'none', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                Send
              </button>
              <button onClick={() => { setShareTarget(null); setShareError(''); }}
                style={{ padding: '9px 12px', borderRadius: 8, background: 'rgba(255,255,255,0.08)', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: 13 }}>
                ✕
              </button>
            </div>
            {shareError && <div style={{ fontSize: 11, color: '#fca5a5', marginTop: 6 }}>{shareError}</div>}
            {shareTarget === 'whatsapp' && (
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 6 }}>Include country code without + or spaces (e.g. 923001234567 for Pakistan)</div>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 10 }}>
        <button className="ur-btn-hover" style={{ ...s.btnPrimary, flex: 1, justifyContent: 'center', display: 'flex' }} onClick={onCreateAnother}>
          + Create Another Authority
        </button>
        <button className="ur-btn-hover" style={{ ...s.btnSecondary, flex: 1, justifyContent: 'center', display: 'flex' }} onClick={() => navigate('/higher-authority/authorities')}>
          View All Authorities
        </button>
      </div>
    </div>
  );
}

// ── Create form ───────────────────────────────────────────────────────────────
function CreateAuthorityInner() {
  const { theme } = useTheme();
  const s = buildStyles(theme);
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  React.useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  const [limits, setLimits] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', authorityType: 'department', department: '' });
  const [loading, setLoading] = useState(false);
  const [limitsLoading, setLimitsLoading] = useState(true);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null);
  const [tempPassword, setTempPassword] = useState('');

  const fetchLimits = () =>
    api.get('/higher-authority/limits').then(r => setLimits(r.data)).catch(() => {}).finally(() => setLimitsLoading(false));

  useEffect(() => { fetchLimits(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/higher-authority/create-authority', form);
      setTempPassword(res.data.temp_password);
      setCreated(res.data.authority);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create authority member');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAnother = () => {
    setCreated(null);
    setTempPassword('');
    setError('');
    setForm({ name: '', email: '', authorityType: 'department', department: '' });
    fetchLimits();
  };

  const uniCount = limits?.authorities?.university?.current ?? 0;
  const uniMax   = limits?.authorities?.university?.max ?? 3;
  const deptMap  = limits?.authorities?.department?.by_department ?? {};
  const availableDepts = DEPARTMENTS.filter(d => !deptMap[d]);

  if (limitsLoading) return <LoadingScreen />;

  return (
    <div style={s.page}>
      <div style={s.bgGrid} /><div style={s.bgOrb1} /><div style={s.bgOrb2} /><div style={s.bgOrb3} />
      <div style={s.layout}>
        <Sidebar collapsed={!sidebarOpen} onToggle={() => setSidebarOpen(o => !o)} mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />
        <div style={{ ...s.main, marginLeft: isMobile ? 0 : (sidebarOpen ? 248 : 0), transition: 'margin-left 0.3s ease' }}>
          <Topbar
            title={created ? 'Authority Member Created' : 'Create Authority Member'}
            subtitle={created ? 'Share credentials with the new authority member' : 'Add a new authority member to the system'}
            onToggleSidebar={() => setSidebarOpen(o => !o)}
            onMobileMenu={() => setMobileOpen(true)}
            actions={
              !created && (
                <button className="ur-btn-hover" style={{ ...s.btnSecondary, fontSize: 12, padding: '6px 14px' }}
                  onClick={() => navigate('/higher-authority/authorities')}>
                  View All Authorities
                </button>
              )
            }
          />
          <div style={s.content}>
            {created ? (
              <SuccessCard created={created} tempPassword={tempPassword} onCreateAnother={handleCreateAnother} navigate={navigate} s={s} />
            ) : (
              <div style={{ maxWidth: 560, margin: '0 auto' }}>
                {/* Quota cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 12, marginBottom: 24 }}>
                  <div style={{ ...s.card, padding: '16px 20px', border: '1px solid rgba(99,102,241,0.3)', background: 'rgba(99,102,241,0.07)' }}>
                    <div style={{ fontSize: 11, color: '#a5b4fc', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>University Authorities</div>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6 }}>
                      <span style={{ fontSize: 28, fontWeight: 800, color: '#fff' }}>{uniCount}</span>
                      <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.4)', marginBottom: 4 }}>/ {uniMax}</span>
                    </div>
                    <div style={{ marginTop: 8, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.1)', overflow: 'hidden' }}>
                      <div style={{ width: `${(uniCount / uniMax) * 100}%`, height: '100%', borderRadius: 2, background: uniCount >= uniMax ? '#ef4444' : '#a5b4fc' }} />
                    </div>
                    {uniCount >= uniMax && <div style={{ fontSize: 10, color: '#fca5a5', marginTop: 5 }}>Limit reached</div>}
                  </div>
                  <div style={{ ...s.card, padding: '16px 20px', border: '1px solid rgba(245,158,11,0.3)', background: 'rgba(245,158,11,0.07)' }}>
                    <div style={{ fontSize: 11, color: '#fcd34d', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>Department Authorities</div>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6 }}>
                      <span style={{ fontSize: 28, fontWeight: 800, color: '#fff' }}>{Object.keys(deptMap).length}</span>
                      <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.4)', marginBottom: 4 }}>/ {DEPARTMENTS.length}</span>
                    </div>
                    <div style={{ marginTop: 8, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.1)', overflow: 'hidden' }}>
                      <div style={{ width: `${(Object.keys(deptMap).length / DEPARTMENTS.length) * 100}%`, height: '100%', borderRadius: 2, background: '#fcd34d' }} />
                    </div>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 5 }}>{availableDepts.length} departments available</div>
                  </div>
                </div>

                {/* Form card */}
                <div style={{ ...s.card, padding: '28px 32px' }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: '#fff', marginBottom: 6 }}>New Authority Member Details</div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginBottom: 24 }}>A temporary password will be auto-generated</div>

                  {error && <div style={{ ...s.alertErr, marginBottom: 18 }}>{error}</div>}

                  <form onSubmit={handleSubmit}>
                    {/* Authority type selector */}
                    <div style={{ marginBottom: 20 }}>
                      <label style={s.label}>Authority Type</label>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 10 }}>
                        {[
                          { value: 'department', label: 'Department Authority', icon: '🏢', sub: 'Chairman — manages one dept', disabled: false },
                          { value: 'university', label: 'University Authority', icon: '🏛️', sub: uniCount >= uniMax ? 'Limit reached' : `${uniCount}/${uniMax} slots used`, disabled: uniCount >= uniMax },
                        ].map(opt => (
                          <button key={opt.value} type="button"
                            disabled={opt.disabled}
                            onClick={() => !opt.disabled && setForm(f => ({ ...f, authorityType: opt.value, department: '' }))}
                            style={{ padding: '14px 16px', borderRadius: 12, cursor: opt.disabled ? 'not-allowed' : 'pointer', textAlign: 'left', opacity: opt.disabled ? 0.45 : 1, transition: 'all 0.2s',
                              background: form.authorityType === opt.value ? (opt.value === 'university' ? 'rgba(99,102,241,0.25)' : 'rgba(245,158,11,0.2)') : 'rgba(255,255,255,0.05)',
                              border: `2px solid ${form.authorityType === opt.value ? (opt.value === 'university' ? 'rgba(99,102,241,0.6)' : 'rgba(245,158,11,0.5)') : 'rgba(255,255,255,0.1)'}`,
                            }}>
                            <div style={{ fontSize: 20, marginBottom: 6 }}>{opt.icon}</div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>{opt.label}</div>
                            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>{opt.sub}</div>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div style={{ marginBottom: 18 }}>
                      <label style={s.label}>Full Name</label>
                      <input style={s.input} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Prof. Dr. Ali Hassan" required />
                    </div>

                    <div style={{ marginBottom: 18 }}>
                      <label style={s.label}>Email Address</label>
                      <input style={s.input} type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="xyz@authority.edu" required />
                    </div>

                    {form.authorityType === 'department' && (
                      <div style={{ marginBottom: 18 }}>
                        <label style={s.label}>Department</label>
                        {availableDepts.length === 0 ? (
                          <div style={{ padding: '12px 14px', borderRadius: 9, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', fontSize: 12, color: COLORS.amber }}>
                            All departments already have authority members assigned.
                          </div>
                        ) : (
                          <select style={s.select} value={form.department} onChange={e => setForm(f => ({ ...f, department: e.target.value }))} required>
                            <option value="">Select a department...</option>
                            {availableDepts.map(d => <option key={d} value={d}>{d}</option>)}
                          </select>
                        )}
                      </div>
                    )}

                    <div style={{ padding: '12px 14px', borderRadius: 10, background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.25)', fontSize: 12, color: 'rgba(255,255,255,0.6)', marginBottom: 22, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                      <span style={{ fontSize: 16, flexShrink: 0 }}>🔑</span>
                      <span>A secure temporary password will be generated automatically. You'll be able to share it with the authority member after creation.</span>
                    </div>

                    <button type="submit" disabled={loading || (form.authorityType === 'department' && availableDepts.length === 0)} className="ur-btn-hover"
                      style={{ ...s.btnPrimary, width: '100%', justifyContent: 'center', display: 'flex', padding: '13px', fontSize: 14, fontWeight: 700, opacity: loading ? 0.7 : 1 }}>
                      {loading ? 'Creating Authority Member...' : 'Create Authority Member'}
                    </button>
                  </form>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CreateAuthority() {
  return <ThemeProvider><CreateAuthorityInner /></ThemeProvider>;
}
